// FTEDN Team App — Client-side feature flag hook
// Same implementation as creator-app — subscribes to Firestore for real-time flag updates.

import { useState, useEffect, useRef } from 'react';
import {
  doc,
  onSnapshot,
} from 'firebase/firestore';
import {
  FLAG_DEFINITIONS,
  resolveFlag,
  type FlagName,
  type FlagLayers,
} from '@ftedn/shared';
import { useAuthStore } from '../stores/auth.store';
import { connectFirestoreEmulatorIfNeeded, firestoreDb } from '../services/firebase';

const globalFlags: Record<string, unknown> = {};
const userFlags: Record<string, unknown> = {};
let globalUnsub: (() => void) | null = null;
let userUnsub: (() => void) | null = null;
let subscriberCount = 0;

function startListeners(userId: string) {
  connectFirestoreEmulatorIfNeeded();

  if (!globalUnsub) {
    globalUnsub = onSnapshot(
      doc(firestoreDb, 'flagOverrides/global/active/flags'),
      (snap) => {
        Object.keys(globalFlags).forEach((k) => delete globalFlags[k]);
        if (snap.exists()) Object.assign(globalFlags, snap.data());
      },
      () => {}
    );
  }

  if (!userUnsub && userId) {
    userUnsub = onSnapshot(
      doc(firestoreDb, `users/${userId}/flags/overrides`),
      (snap) => {
        Object.keys(userFlags).forEach((k) => delete userFlags[k]);
        if (snap.exists()) Object.assign(userFlags, snap.data());
      },
      () => {}
    );
  }
}

function stopListeners() {
  if (globalUnsub) { globalUnsub(); globalUnsub = null; }
  if (userUnsub) { userUnsub(); userUnsub = null; }
}

/** Resolve a single flag with real-time updates. */
export function useFlag<N extends FlagName>(name: N): unknown {
  const { user } = useAuthStore();
  const def = FLAG_DEFINITIONS[name];
  const [value, setValue] = useState<unknown>(def?.default);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    subscriberCount++;
    startListeners(user.id);
    return () => {
      subscriberCount--;
      if (subscriberCount <= 0) { subscriberCount = 0; stopListeners(); }
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !def) return;
    const interval = setInterval(() => {
      if (!mounted.current) return;
      const layers: FlagLayers = { env: globalFlags, user: userFlags };
      const resolved = resolveFlag(name, layers);
      setValue((prev: unknown) => prev === resolved ? prev : resolved);
    }, 500);
    const layers: FlagLayers = { env: globalFlags, user: userFlags };
    setValue(resolveFlag(name, layers));
    return () => clearInterval(interval);
  }, [user?.id, name, def]);

  return value;
}

/** Get all flags at once. */
export function useAllFlags(): Record<string, unknown> {
  const { user } = useAuthStore();
  const [flags, setFlags] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (!user?.id) return;
    subscriberCount++;
    startListeners(user.id);
    const interval = setInterval(() => {
      const layers: FlagLayers = { env: globalFlags, user: userFlags };
      const resolved: Record<string, unknown> = {};
      for (const name of Object.keys(FLAG_DEFINITIONS) as FlagName[]) {
        resolved[name] = resolveFlag(name, layers);
      }
      setFlags(resolved);
    }, 500);
    const layers: FlagLayers = { env: globalFlags, user: userFlags };
    const resolved: Record<string, unknown> = {};
    for (const name of Object.keys(FLAG_DEFINITIONS) as FlagName[]) {
      resolved[name] = resolveFlag(name, layers);
    }
    setFlags(resolved);
    return () => {
      clearInterval(interval);
      subscriberCount--;
      if (subscriberCount <= 0) { subscriberCount = 0; stopListeners(); }
    };
  }, [user?.id]);

  return flags;
}
